import { useCallback, useContext } from "react";
import { internalFetcher } from "core/fetcher";
import { usePromise } from "react-use";
import { CognitoAuthContext } from "components/Authentication/CognitoAuthContext";

/**
 * Provides a callback function which can be used to fetch
 * @param fetcher callback function that should be used to do the actual fetch, should take in url and init parameters
 * @returns {{callbackFunction: (function(*=): Promise<unknown>)}}
 *
 * callbackFunction - the callback function, takes a body object and returns an abort function and a response promise
 */
export function useFetchV2(fetcher = internalFetcher) {
  const mounted = usePromise();
  const { getToken } = useContext(CognitoAuthContext);

  /* fetches using the url provided to the hook and the init object supplied as params.
   * Calls onCompleted when successful.
   * Calls onError on failure. */
  const callbackFunction = useCallback(
    (url, init) => {
      const fetchFromUrl = async () => {
        let response = null;
        try {
          response = await mounted(fetcher(url, init, getToken));
        } catch (e) {
          // We don't care about abort errors as they will happen any time we abort a request
          if (e instanceof DOMException && e.name === "AbortError") {
            return Promise.resolve(null);
          } else {
            throw e;
          }
        }

        if (!response.ok) {
          // if response was not ok then there is no response to return
          // advanced error handling should be done using onError
          return Promise.reject(response);
        }

        const isJson = response.headers
          .get("content-type")
          ?.includes("application/json");

        const responseJson =
          response.status !== 204 && isJson ? await response.json() : null;

        return Promise.resolve(responseJson);
      };
      return fetchFromUrl();
    },
    [fetcher, getToken, mounted]
  );
  return { callbackFunction };
}
